// src/components/quickMessage/MultiPill.tsx -- ≥2-inbox-message Quick Message
// surface. Renders the flashing pill with the unread-count badge, the row of
// name chips (one per unique sender), and -- once a chip is selected -- the
// matching Banner in expanded state directly below.
//
// Dedupe rule: one chip per `sender.id`. A chip's blue dot lights when at
// least one of that sender's inbox rows is still unread (readAt === null) --
// per gotcha #5 ("the pill's unread count is a strict subset of name chips").
// The banner shown when a chip is selected is the newest message from that
// sender (gotcha #6 -- chip = the person, not a single message).

import type { CSSProperties } from "react";
import { useState } from "react";
import { C, F } from "../../theme";
import {
  QM,
  senderInitials,
  senderShortName,
  type QuickMessage,
  type ThreadMessage,
} from "../../lib/quickMessages";
import { Banner } from "./Banner";

export interface MultiPillProps {
  messages: QuickMessage[];
  tick: number;
  busy: boolean;
  thread: ThreadMessage[] | null;
  threadOpen: boolean;
  threadLoading: boolean;
  threadError: string | null;
  /** Which message is currently expanded. null = no chip selected. */
  expandedId: string | null;
  onExpand: (message: QuickMessage) => void;
  onCollapse: () => void;
  onToggleThread: () => void;
  onDismiss: (message: QuickMessage) => void;
  onSendReply: (message: QuickMessage, body: string) => void;
}

interface ChipGroup {
  key: string;
  newest: QuickMessage;
  hasUnread: boolean;
}

const PILL_KEYFRAMES = `
@keyframes qm-pill-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(61,111,168,0.55); }
  70%  { box-shadow: 0 0 0 7px rgba(61,111,168,0); }
  100% { box-shadow: 0 0 0 0 rgba(61,111,168,0); }
}
@keyframes qm-pill-shimmer {
  0%, 100% { transform: translateX(-100%); }
  50%      { transform: translateX(100%); }
}
`;

export function MultiPill({
  messages,
  tick,
  busy,
  thread,
  threadOpen,
  threadLoading,
  threadError,
  expandedId,
  onExpand,
  onCollapse,
  onToggleThread,
  onDismiss,
  onSendReply,
}: MultiPillProps) {
  const [pillOpen, setPillOpen] = useState(true);
  const unreadCount = messages.filter((m) => m.readAt === null).length;
  const chipGroups = buildChipGroups(messages);
  const selectedMessage = expandedId
    ? messages.find((m) => m.id === expandedId) ?? null
    : null;

  function togglePill() {
    if (pillOpen && selectedMessage) onCollapse();
    setPillOpen((v) => !v);
  }

  function selectChip(group: ChipGroup) {
    if (selectedMessage && selectedMessage.id === group.newest.id) {
      onCollapse();
      return;
    }
    onExpand(group.newest);
  }

  return (
    <div>
      <button
        type="button"
        onClick={togglePill}
        aria-expanded={pillOpen}
        aria-controls="qm-name-chips"
        style={pillStyle}
      >
        <span aria-hidden="true" style={pillShimmerStyle} />
        <span style={pillIconStyle} aria-hidden="true">
          <PlaneIcon />
        </span>
        <span style={pillTextWrapStyle}>
          <span style={pillLabelRowStyle}>
            <span style={pillLabelStyle}>Quick messages</span>
            <span style={pillCountStyle}>{unreadCount}</span>
          </span>
          <span style={pillMetaStyle}>
            <PulseDot small />
            {unreadCount} unread
          </span>
        </span>
        <span aria-hidden="true" style={pillChevronStyle}>
          {pillOpen ? "⌃" : "›"}
        </span>
      </button>

      {pillOpen && (
        <div id="qm-name-chips" style={chipsRowStyle}>
          {chipGroups.map((g) => {
            const selected = selectedMessage?.id === g.newest.id;
            return (
              <ChipButton
                key={g.key}
                group={g}
                selected={selected}
                onClick={() => selectChip(g)}
              />
            );
          })}
        </div>
      )}

      {pillOpen && selectedMessage && (
        <div style={{ marginTop: 12 }}>
          <Banner
            message={selectedMessage}
            expanded
            tick={tick}
            thread={thread}
            threadOpen={threadOpen}
            threadLoading={threadLoading}
            threadError={threadError}
            busy={busy}
            onExpand={() => {
              /* already expanded inside multi-pill mode -- no-op */
            }}
            onCollapse={onCollapse}
            onToggleThread={onToggleThread}
            onDismiss={() => onDismiss(selectedMessage)}
            onSendReply={(body) => onSendReply(selectedMessage, body)}
          />
        </div>
      )}

      <style>{PILL_KEYFRAMES}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip
// ---------------------------------------------------------------------------

function ChipButton({
  group,
  selected,
  onClick,
}: {
  group: ChipGroup;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={chipStyle(selected)}
    >
      <span style={chipAvatarStyle(selected)}>
        {senderInitials(group.newest.sender)}
      </span>
      <span style={chipNameStyle(selected)}>
        {senderShortName(group.newest.sender)}
      </span>
      {group.hasUnread && !selected && <span style={chipDotStyle} aria-hidden="true" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

function buildChipGroups(messages: QuickMessage[]): ChipGroup[] {
  const map = new Map<string, ChipGroup>();
  for (const m of messages) {
    const key =
      m.sender.id !== null ? `u${m.sender.id}` : `orphan-${m.id}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        key,
        newest: m,
        hasUnread: m.readAt === null,
      });
      continue;
    }
    // messages arrive newest-first from the API, so the existing entry is
    // already newer than m -- don't overwrite newest. But hasUnread folds
    // across all of this sender's rows.
    if (m.readAt === null) existing.hasUnread = true;
  }
  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// Sub-icons
// ---------------------------------------------------------------------------

function PlaneIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function PulseDot({ small = false }: { small?: boolean }) {
  const s = small ? 7 : 10;
  return (
    <span
      aria-hidden="true"
      style={{
        width: s,
        height: s,
        borderRadius: "50%",
        background: QM.base,
        animation: "qm-pill-pulse 1.6s ease-out infinite",
        display: "inline-block",
        flexShrink: 0,
        marginRight: 4,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const pillStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "11px 16px",
  background: C.card,
  border: `1.5px solid ${QM.base}`,
  borderRadius: 14,
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(61,111,168,0.10)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  fontFamily: F.body,
  textAlign: "left",
  WebkitTapHighlightColor: "transparent",
};

const pillShimmerStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(90deg, transparent, rgba(61,111,168,0.06), transparent)",
  animation: "qm-pill-shimmer 2.4s ease-in-out infinite",
  pointerEvents: "none",
};

const pillIconStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 10,
  background: `linear-gradient(135deg, ${QM.base}, ${QM.dark})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  flexShrink: 0,
  position: "relative",
};

const pillTextWrapStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  position: "relative",
};

const pillLabelRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const pillLabelStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 15,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
};

const pillCountStyle: CSSProperties = {
  background: QM.base,
  color: "white",
  fontFamily: F.mono,
  fontSize: 11,
  fontWeight: 800,
  padding: "2px 8px",
  borderRadius: 999,
  letterSpacing: 0.4,
};

const pillMetaStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 700,
  color: QM.dark,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  marginTop: 1,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const pillChevronStyle: CSSProperties = {
  color: QM.dark,
  fontSize: 18,
  fontWeight: 700,
  position: "relative",
  flexShrink: 0,
};

const chipsRowStyle: CSSProperties = {
  marginTop: 10,
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  padding: "0 4px",
};

function chipStyle(selected: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px 7px 8px",
    background: selected ? QM.base : C.card,
    border: `1px solid ${selected ? QM.base : QM.light}`,
    borderRadius: 999,
    cursor: "pointer",
    boxShadow: selected
      ? "0 2px 10px rgba(61,111,168,0.30)"
      : "0 1px 4px rgba(61,111,168,0.08)",
    fontFamily: F.body,
    WebkitTapHighlightColor: "transparent",
  };
}

function chipAvatarStyle(selected: boolean): CSSProperties {
  return {
    width: 22,
    height: 22,
    borderRadius: 8,
    background: selected
      ? "rgba(255,255,255,0.2)"
      : `linear-gradient(135deg, ${C.brassLt}, ${C.brass})`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: F.serif,
    fontWeight: 700,
    fontSize: 10,
    flexShrink: 0,
  };
}

function chipNameStyle(selected: boolean): CSSProperties {
  return {
    fontFamily: F.body,
    fontSize: 13,
    fontWeight: 700,
    color: selected ? "white" : C.ink,
    letterSpacing: -0.1,
    whiteSpace: "nowrap",
  };
}

const chipDotStyle: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: QM.base,
  marginLeft: 2,
  flexShrink: 0,
};
